import React, { useState, useEffect, useRef } from 'react';
import { processYouTubeUrl, generateEmbedUrl, extractVideoId } from '../utils/youtubeUtils';
import '../styles/youtubeEmbed.css';

/**
 * YouTubeEmbed component for embedding and displaying YouTube videos of various formats
 * Handles both standard landscape videos and vertical Shorts
 * 
 * @param {Object} props - Component props
 * @param {string} props.videoUrl - The YouTube video URL or ID
 * @param {string} props.format - Optional format override ('landscape', 'portrait', 'square')
 * @param {Object} props.embedOptions - Optional YouTube embed parameters
 * @returns {JSX.Element} YouTubeEmbed component
 */
const YouTubeEmbed = ({ videoUrl, videoId, format, embedOptions = {} }) => {
  // State to track video information and loading status
  const [videoData, setVideoData] = useState({ videoId: videoId || null, format: format || 'landscape' });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const iframeRef = useRef(null);
  
  // Process the video URL to extract ID and determine format
  useEffect(() => {
    // If videoId is directly provided, use it
    if (videoId) {
      setVideoData({
        videoId,
        format: format || 'landscape'
      });
      setIsLoading(false);
      return;
    }
    
    // Otherwise process the URL
    if (!videoUrl) {
      setError('No video URL provided');
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Process the URL to extract video ID and check if it's a Short
      const { videoId, isShort, error: urlError } = processYouTubeUrl(videoUrl);
      
      if (urlError || !videoId) {
        console.error('YouTube URL processing error:', urlError || 'Invalid video ID');
        setError(urlError || 'Invalid YouTube URL');
        setIsLoading(false);
        return;
      }
      
      // Determine video format - prioritize explicit format prop, then URL detection
      let detectedFormat;
      if (format) {
        // Use explicitly provided format
        detectedFormat = format;
      } else if (isShort) {
        // Detected as a YouTube Short
        detectedFormat = 'portrait';
      } else {
        // Default to landscape format
        detectedFormat = 'landscape';
      }
      
      // Update state with extracted information
      setVideoData({
        videoId,
        format: detectedFormat
      });
      
      setIsLoading(false);
    } catch (err) {
      console.error('Error processing YouTube URL:', err);
      setError('Unable to process video URL');
      setIsLoading(false);
    }
  }, [videoUrl, videoId, format]);
  
  // Display loading spinner while processing
  if (isLoading) {
    return (
      <div className="youtube-loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }
  
  // Display error message if something went wrong
  if (error) {
    return (
      <div className="youtube-error">
        <p>{error}</p>
      </div>
    );
  }
  
  // If no video ID was extracted, show unavailable message
  if (!videoData.videoId) {
    return (
      <div className="video-unavailable">
        No video available.
      </div>
    );
  }
  
  // Enhanced parameters for vertical videos to reduce letterboxing
  const enhancedOptions = {
    ...embedOptions,
    // Add specific parameters for portrait videos to improve display
    ...(videoData.format === 'portrait' ? {
      modestBranding: true,
      showInfo: false,
      rel: false
    } : {})
  };
  
  // Create the YouTube embed URL with appropriate options
  const embedUrl = generateEmbedUrl(videoData.videoId, enhancedOptions);
  
  // Render different container structure based on video format
  if (videoData.format === 'portrait') {
    // Enhanced structure for vertical videos to fix letterboxing on desktop
    return (
      <div className="youtube-container">
        <div className="youtube-portrait-container">
          <div className="youtube-portrait">
            <iframe
              ref={iframeRef}
              className="youtube-embed"
              src={embedUrl}
              title="YouTube video player"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              onLoad={() => setIsLoading(false)}
              onError={(e) => {
                console.error('YouTube iframe loading error:', e);
                setError('Error loading the video. Please try again later.');
              }}
            ></iframe>
          </div>
        </div>
      </div>
    );
  }
  
  // For landscape and square videos, use standard container structure
  return (
    <div className="youtube-container">
      <div 
        className={`youtube-${videoData.format}`}
        data-testid={`youtube-container-${videoData.format}`}
      >
        <iframe
          ref={iframeRef}
          className="youtube-embed"
          src={embedUrl}
          title="YouTube video player"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          onLoad={() => setIsLoading(false)}
          onError={(e) => {
            console.error('YouTube iframe loading error:', e);
            setError('Error loading the video. Please try again later.');
          }}
        ></iframe>
      </div>
    </div>
  );
};

export default YouTubeEmbed;
